import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  ButtonDropdown,
  Form,
  FormField,
  Input,
  Link,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  StatusIndicator,
  TextFilter,
  Toggle,
} from '@cloudscape-design/components';
import {
  PageLayout,
  PageSkeleton,
  PlatformTable,
  EmptyState,
  useAuth,
  useRoles,
  useNotify,
  createApiClient,
} from '@deloitte-us-consulting/gp-sdk';
import {
  APP_CATEGORIES,
  APP_LIFECYCLES,
  categoryById,
  lifecycleById,
  type AppCategoryId,
  type AppLifecycleId,
} from './home/appStoreVocabulary';
import { appLifecycle } from '../remotes/registry';
import {
  filterApps,
  intakeHref,
  joinRegistry,
  NO_FILTERS,
  repoLabel,
  type AppFilters,
  type AppRow,
  type AppStatus,
  type IntakeRef,
} from './appsModel';

/** Entry shape served by GET /api/registry/all (admin only) and PUT /api/registry/:appId. */
interface RegistryEntry {
  appId: string;
  displayName: string;
  remoteUrl?: string;
  /** Set on a LINKED app instead of remoteUrl — an app hosted (and authed) outside
   *  this platform. The shell lists it but opens it in a new tab; never both. */
  externalUrl?: string;
  navIcon?: string;
  navOrder?: number;
  requiredRole?: string[];
  enabled: boolean;
  aiEnabled?: boolean;
  /** Never edited here. Listed so it's clear the form deliberately doesn't send it:
   *  the API keeps it when absent (keep-or-clear). */
  requiredAlliance?: string[];
  category?: AppCategoryId;
  tagline?: string;
  ownerTeam?: string;
  lifecycle?: AppLifecycleId;
  /** Retired by lifecycle (ADR 0043); only an older API still serves it. Read, never sent. */
  earlyWip?: boolean;
  description?: string;
  emailEnabled?: boolean;
  emailLinkDomains?: string[];
  emailServiceRoleArn?: string;
  emailDailyCap?: number;
}

/** Editable form state — appId is only settable when registering a brand-new app. */
interface AppForm {
  appId: string;
  displayName: string;
  remoteUrl: string;
  externalUrl: string;
  navIcon: string;
  navOrder: string;
  requiredRole: string[];
  aiEnabled: boolean;
  category: AppCategoryId | '';
  tagline: string;
  ownerTeam: string;
  lifecycle: AppLifecycleId | '';
  description: string;
  emailEnabled: boolean;
  emailLinkDomains: string;
  emailServiceRoleArn: string;
  emailDailyCap: string;
}

const EMPTY_FORM: AppForm = {
  appId: '',
  displayName: '',
  remoteUrl: '',
  externalUrl: '',
  navIcon: '',
  navOrder: '',
  requiredRole: [],
  aiEnabled: false,
  category: '',
  tagline: '',
  ownerTeam: '',
  // A new app is rarely finished; the admin moves it on as it matures.
  lifecycle: 'in-development',
  description: '',
  emailEnabled: false,
  emailLinkDomains: '',
  emailServiceRoleArn: '',
  emailDailyCap: '',
};

const APP_ID_PATTERN = /^[a-z0-9-]+$/;

const TAGLINE_MAX = 90;
const OWNER_TEAM_MAX = 60;

// Same patterns the API enforces — see api/src/routes/registry.ts.
const EMAIL_LINK_HOST = /^(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}$/;
const IAM_ROLE_ARN = /^arn:aws[a-z-]*:iam::\d{12}:role\/[\w+=,.@/-]+$/;

/** A small colored dot for the category dropdown. */
const categoryDot = (color: string) => (
  <svg viewBox="0 0 16 16" focusable="false" aria-hidden="true">
    <circle cx="8" cy="8" r="5" fill={color} />
  </svg>
);

const CATEGORY_OPTIONS = [
  { value: '', label: 'None' },
  ...APP_CATEGORIES.map((c) => ({ value: c.id, label: c.label, iconSvg: categoryDot(c.bar) })),
];

const STAGE_OPTIONS = [
  { value: '', label: 'Not set' },
  ...APP_LIFECYCLES.map((s) => ({ value: s.id, label: s.label, iconSvg: categoryDot(s.dot) })),
];

const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

const PAGE_SIZE = 20;

interface FilterOption { value: string; label: string }
const withAll = (label: string, rest: FilterOption[]): FilterOption[] => [{ value: '', label }, ...rest];
const STATUS_OPTIONS = withAll('Any status', [
  { value: 'live', label: 'Live' },
  { value: 'pending', label: 'Approved — pending deploy' },
  { value: 'disabled', label: 'Disabled' },
]);
const TYPE_OPTIONS = withAll('Any type', [
  { value: 'Hosted', label: 'Hosted' },
  { value: 'Linked', label: 'Linked' },
]);
const AI_OPTIONS = withAll('AI: any', [
  { value: 'yes', label: 'AI enabled' },
  { value: 'no', label: 'AI off' },
]);
const CATEGORY_FILTER_OPTIONS = withAll('Any category', [
  ...APP_CATEGORIES.map((c) => ({ value: c.id, label: c.label })),
  { value: 'none', label: 'No category' },
]);

function statusIndicator(status: AppStatus) {
  if (status === 'live') return <StatusIndicator type="success" wrapText={false}>Live</StatusIndicator>;
  if (status === 'pending') return <StatusIndicator type="in-progress" wrapText={false}>Approved — pending deploy</StatusIndicator>;
  return <StatusIndicator type="stopped" wrapText={false}>Disabled</StatusIndicator>;
}

/** One Select of the filter bar: picks from `options`, '' meaning no filter. */
function FilterSelect({ options, value, label, onChange }: {
  options: FilterOption[];
  value: string;
  label: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      selectedOption={options.find((o) => o.value === value) ?? options[0] ?? null}
      options={options}
      ariaLabel={label}
      onChange={({ detail }) => onChange(detail.selectedOption.value ?? '')}
    />
  );
}

function toForm(entry: RegistryEntry): AppForm {
  return {
    appId: entry.appId,
    displayName: entry.displayName,
    remoteUrl: entry.remoteUrl ?? '',
    externalUrl: entry.externalUrl ?? '',
    navIcon: entry.navIcon ?? '',
    navOrder: entry.navOrder !== undefined ? String(entry.navOrder) : '',
    requiredRole: entry.requiredRole ?? [],
    aiEnabled: entry.aiEnabled ?? false,
    category: categoryById(entry.category)?.id ?? '',
    tagline: entry.tagline ?? '',
    ownerTeam: entry.ownerTeam ?? '',
    lifecycle: lifecycleById(appLifecycle(entry))?.id ?? '',
    description: entry.description ?? '',
    emailEnabled: entry.emailEnabled ?? false,
    emailLinkDomains: (entry.emailLinkDomains ?? []).join(', '),
    emailServiceRoleArn: entry.emailServiceRoleArn ?? '',
    emailDailyCap: entry.emailDailyCap !== undefined ? String(entry.emailDailyCap) : '',
  };
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

/**
 * Admin dashboard for the app registry — register, edit, enable/disable, and
 * remove apps that the shell offers via Module Federation. Gated to platform
 * admins (the Admin role); the API also enforces the same gate server-side. An app
 * only shows up in the shell once it's registered here AND explicitly enabled.
 */
export default function AppRegistryAdmin() {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Registry:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);
  const navigate = useNavigate();

  const [apps, setApps] = useState<RegistryEntry[] | null>(null);
  // The onboarding submissions, joined by appId for the repo + intake links. Best
  // effort: the registry stays usable (without those links) if this call fails.
  const [intakes, setIntakes] = useState<IntakeRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AppFilters>(NO_FILTERS);
  const [pageIndex, setPageIndex] = useState(1);

  const [editing, setEditing] = useState<RegistryEntry | null>(null);
  const [form, setForm] = useState<AppForm>(EMPTY_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RegistryEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [registry, onboarding] = await Promise.allSettled([
        api.get<RegistryEntry[]>('/api/registry/all'),
        api.get<IntakeRef[]>('/api/onboarding/all'),
      ]);
      if (registry.status === 'rejected') throw registry.reason;
      setApps(registry.value);
      setIntakes(onboarding.status === 'fulfilled' ? onboarding.value : []);
    } catch (e) {
      notify({ type: 'error', header: 'Could not load app registry', content: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    load();
    // Load once when admin status / api client settle. Intentionally NOT depending
    // on `notify` — it's a fresh function each render, which would re-run this
    // effect on every render.
  }, [api, isAllowed]);

  if (!isAllowed) {
    return (
      <PageLayout title="App Registry">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </PageLayout>
    );
  }
  if (loading) {
    return (
      <PageLayout title="App Registry">
        <PageSkeleton label="Loading app registry…" />
      </PageLayout>
    );
  }

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };
  const openEdit = (entry: RegistryEntry) => {
    setEditing(entry);
    setForm(toForm(entry));
    setModalOpen(true);
  };

  const appIdError = (): string | undefined => {
    if (editing) return undefined; // appId is fixed once created
    const v = form.appId.trim();
    if (!v) return 'App ID is required.';
    if (v.length > 64) return 'App ID must be 64 characters or fewer.';
    if (!APP_ID_PATTERN.test(v)) return 'App ID must use only lowercase letters, digits, and hyphens.';
    return undefined;
  };
  const displayNameError = (): string | undefined =>
    form.displayName.trim() ? undefined : 'Display name is required.';
  const navOrderError = (): string | undefined => {
    if (!form.navOrder.trim()) return undefined;
    return Number.isInteger(Number(form.navOrder)) ? undefined : 'Nav order must be a whole number.';
  };
  // A LINKED app (externalUrl) lives outside the platform with its own auth — the
  // API rejects an entry that also carries a remoteUrl (hosted XOR linked), and an
  // externalUrl must be an absolute https:// URL, never a same-origin path.
  const externalUrlError = (): string | undefined => {
    const v = form.externalUrl.trim();
    if (!v) return undefined;
    if (form.remoteUrl.trim()) return 'An app is either hosted (Remote URL) or linked (External URL), not both.';
    try {
      if (new URL(v).protocol !== 'https:') return 'External URL must use https://.';
    } catch {
      return 'External URL must be an absolute https:// URL.';
    }
    return undefined;
  };
  const taglineError = (): string | undefined =>
    form.tagline.trim().length > TAGLINE_MAX ? `Tagline must be ${TAGLINE_MAX} characters or fewer.` : undefined;
  const ownerTeamError = (): string | undefined =>
    form.ownerTeam.trim().length > OWNER_TEAM_MAX
      ? `Owner team must be ${OWNER_TEAM_MAX} characters or fewer.`
      : undefined;
  const emailDomainsError = (): string | undefined => {
    const bad = csv(form.emailLinkDomains).find((d) => !EMAIL_LINK_HOST.test(d.toLowerCase()));
    return bad ? `Not a hostname: ${bad}` : undefined;
  };
  const emailRoleError = (): string | undefined =>
    form.emailServiceRoleArn.trim() && !IAM_ROLE_ARN.test(form.emailServiceRoleArn.trim())
      ? 'Must be an IAM role ARN (arn:aws:iam::<account>:role/<name>).'
      : undefined;
  const emailCapError = (): string | undefined => {
    const v = form.emailDailyCap.trim();
    if (!v) return undefined;
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 && n <= 10000 ? undefined : 'Enter a whole number from 1 to 10000, or leave blank.';
  };

  const formValid =
    !appIdError() &&
    !displayNameError() &&
    !navOrderError() &&
    !externalUrlError() &&
    !taglineError() &&
    !ownerTeamError() &&
    !emailDomainsError() &&
    !emailRoleError() &&
    !emailCapError();

  const save = async () => {
    if (!formValid) return;
    const appId = editing ? editing.appId : form.appId.trim();
    setSaving(true);
    try {
      const body = {
        displayName: form.displayName.trim(),
        remoteUrl: form.remoteUrl.trim() || undefined,
        externalUrl: form.externalUrl.trim() || undefined,
        navIcon: form.navIcon.trim() || undefined,
        navOrder: form.navOrder.trim() ? Number(form.navOrder) : undefined,
        requiredRole: form.requiredRole.length ? form.requiredRole : undefined,
        aiEnabled: form.aiEnabled,
        // Always sent, as a value or null: the API is keep-or-clear, so null is the
        // only way to clear a field. requiredAlliance is deliberately never sent.
        category: form.category || null,
        tagline: form.tagline.trim() || null,
        ownerTeam: form.ownerTeam.trim() || null,
        lifecycle: form.lifecycle || null,
        description: form.description.trim() || undefined,
        emailEnabled: form.emailEnabled,
        // Keep-or-clear, like category/tagline: null is the only way to clear.
        emailLinkDomains: csv(form.emailLinkDomains).length ? csv(form.emailLinkDomains).map((d) => d.toLowerCase()) : null,
        emailServiceRoleArn: form.emailServiceRoleArn.trim() || null,
        emailDailyCap: form.emailDailyCap.trim() ? Number(form.emailDailyCap.trim()) : null,
      };
      await api.put<RegistryEntry>(`/api/registry/${encodeURIComponent(appId)}`, body);
      notify({
        type: 'success',
        header: editing ? 'App updated' : 'App registered',
        content: `${form.displayName.trim()} (${appId}) saved.`,
      });
      setModalOpen(false);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = async (entry: RegistryEntry, enabled: boolean) => {
    setPendingAction(`${entry.appId}:${enabled ? 'enable' : 'disable'}`);
    try {
      await api.post<RegistryEntry>(`/api/registry/${encodeURIComponent(entry.appId)}/${enabled ? 'enable' : 'disable'}`);
      notify({
        type: 'success',
        header: enabled ? 'App enabled' : 'App disabled',
        content: `${entry.displayName} is now ${enabled ? 'enabled' : 'disabled'}.`,
      });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Update failed', content: (e as Error).message });
    } finally {
      setPendingAction(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete<void>(`/api/registry/${encodeURIComponent(deleteTarget.appId)}`);
      notify({ type: 'success', header: 'App deleted', content: `${deleteTarget.displayName} was removed from the registry.` });
      setDeleteTarget(null);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Delete failed', content: (e as Error).message });
    } finally {
      setDeleting(false);
    }
  };

  const rows: AppRow<RegistryEntry>[] = joinRegistry(apps ?? [], intakes);
  const filtered = filterApps(rows, filters);
  const pagesCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(pageIndex, pagesCount);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const isFiltering = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS);
  const setFilter = (patch: Partial<AppFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPageIndex(1);
  };
  const followInternal = (href: string) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    navigate(href);
  };

  return (
    <PageLayout
      title="App Registry"
      description="Every app on the platform, in one place. An app only appears for users once it's registered here AND enabled."
      actions={
        <Button variant="primary" onClick={openCreate}>
          Register new app
        </Button>
      }
    >
      {!apps || apps.length === 0 ? (
        <EmptyState
          title="No apps registered yet"
          description="Register an app to make it available in the shell."
          action={
            <Button variant="primary" onClick={openCreate}>
              Register new app
            </Button>
          }
        />
      ) : (
        <PlatformTable
          items={pageItems}
          title="Apps"
          totalCount={filtered.length}
          variant="container"
          wrapLines
          filter={
            <SpaceBetween size="xs" direction="horizontal">
              <TextFilter
                filteringText={filters.text}
                filteringPlaceholder="Search by name, ID, repo, owner or contact"
                filteringAriaLabel="Search apps"
                countText={isFiltering ? `${filtered.length} ${filtered.length === 1 ? 'match' : 'matches'}` : ''}
                onChange={({ detail }) => setFilter({ text: detail.filteringText })}
              />
              <FilterSelect label="Status" options={STATUS_OPTIONS} value={filters.status} onChange={(v) => setFilter({ status: v as AppFilters['status'] })} />
              <FilterSelect label="Type" options={TYPE_OPTIONS} value={filters.type} onChange={(v) => setFilter({ type: v as AppFilters['type'] })} />
              <FilterSelect label="Category" options={CATEGORY_FILTER_OPTIONS} value={filters.category} onChange={(v) => setFilter({ category: v })} />
              <FilterSelect label="AI" options={AI_OPTIONS} value={filters.ai} onChange={(v) => setFilter({ ai: v as AppFilters['ai'] })} />
              {isFiltering && (
                <Button variant="link" onClick={() => setFilter(NO_FILTERS)}>
                  Clear filters
                </Button>
              )}
            </SpaceBetween>
          }
          pagination={<Pagination currentPageIndex={currentPage} pagesCount={pagesCount} onChange={({ detail }) => setPageIndex(detail.currentPageIndex)} />}
          empty={
            <EmptyState
              title="No apps match these filters"
              description="Try a different search, or clear the filters."
              action={<Button onClick={() => setFilter(NO_FILTERS)}>Clear filters</Button>}
            />
          }
          columnDefinitions={[
            {
              header: 'App',
              cell: (a) => (
                <div>
                  <div><strong>{a.displayName}</strong></div>
                  <Box variant="small" color="text-body-secondary">{a.appId}</Box>
                </div>
              ),
            },
            { header: 'Status', cell: (a) => statusIndicator(a.status) },
            { header: 'Type', cell: (a) => a.type },
            { header: 'Category', cell: (a) => categoryById(a.category)?.label ?? '—' },
            { header: 'Stage', cell: (a) => lifecycleById(appLifecycle(a))?.label ?? '—' },
            {
              header: 'Source',
              cell: (a) =>
                a.intake ? (
                  <div>
                    <Link external href={a.intake.repoUrl}>{repoLabel(a.intake.repoUrl)}</Link>
                    <div>
                      <Link href={intakeHref(a.intake.id)} onFollow={followInternal(intakeHref(a.intake.id))}>
                        Intake submission
                      </Link>
                    </div>
                  </div>
                ) : (
                  <Box color="text-body-secondary">Registered directly</Box>
                ),
            },
            {
              header: 'Owner',
              cell: (a) => a.ownerTeam || a.intake?.pointOfContactName || a.intake?.pointOfContactEmail || '—',
            },
            {
              header: 'Access',
              cell: (a) => [...(a.requiredRole ?? []), ...(a.requiredAlliance ?? [])].join(', ') || 'Everyone',
            },
            { header: 'AI', cell: (a) => (a.aiEnabled ? 'Yes' : '—') },
            {
              header: 'Actions',
              cell: (a) => (
                <SpaceBetween size="xs" direction="horizontal">
                  <Button variant="inline-link" onClick={() => openEdit(a)}>
                    Edit
                  </Button>
                  <ButtonDropdown
                    variant="inline-icon"
                    ariaLabel={`More actions for ${a.displayName}`}
                    expandToViewport
                    loading={pendingAction?.startsWith(`${a.appId}:`) ?? false}
                    items={[
                      { id: 'toggle', text: a.enabled ? 'Disable' : 'Enable' },
                      { id: 'delete', text: 'Delete' },
                    ]}
                    onItemClick={({ detail }) => (detail.id === 'delete' ? setDeleteTarget(a) : setEnabled(a, !a.enabled))}
                  />
                </SpaceBetween>
              ),
            },
          ]}
        />
      )}

      <Modal
        visible={modalOpen}
        onDismiss={() => setModalOpen(false)}
        header={editing ? `Edit ${editing.appId}` : 'Register new app'}
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={saving} disabled={!formValid} onClick={save}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="App ID" description="Lowercase letters, digits, and hyphens only. Cannot be changed later." errorText={appIdError()}>
              <Input
                value={form.appId}
                disabled={!!editing}
                onChange={({ detail }) => setForm((f) => ({ ...f, appId: detail.value }))}
                placeholder="e.g. my-app"
              />
            </FormField>
            <FormField label="Display name" errorText={displayNameError()}>
              <Input value={form.displayName} onChange={({ detail }) => setForm((f) => ({ ...f, displayName: detail.value }))} />
            </FormField>
            <FormField label="Remote URL" description="Absolute URL or same-origin path to a hosted app's remoteEntry.js. Leave empty for a headless/chat-only app, or if this is a linked app (External URL below).">
              <Input value={form.remoteUrl} onChange={({ detail }) => setForm((f) => ({ ...f, remoteUrl: detail.value }))} />
            </FormField>
            <FormField
              label="External URL"
              description="For a LINKED app that lives outside this platform (its own auth/hosting) — an absolute https:// URL. The shell lists it but opens it in a new tab instead of mounting it. Leave empty for a hosted app."
              errorText={externalUrlError()}
            >
              <Input
                value={form.externalUrl}
                onChange={({ detail }) => setForm((f) => ({ ...f, externalUrl: detail.value }))}
                placeholder="https://example.com/"
              />
            </FormField>
            <FormField label="Nav icon" description="Cloudscape icon name (optional).">
              <Input value={form.navIcon} onChange={({ detail }) => setForm((f) => ({ ...f, navIcon: detail.value }))} />
            </FormField>
            <FormField label="Nav order" errorText={navOrderError()}>
              <Input
                type="number"
                value={form.navOrder}
                onChange={({ detail }) => setForm((f) => ({ ...f, navOrder: detail.value }))}
              />
            </FormField>
            <FormField
              label="Required role(s)"
              description="Comma-separated role ids — a user needs at least one to see this app (optional)."
            >
              <Input
                value={form.requiredRole.join(', ')}
                onChange={({ detail }) => setForm((f) => ({ ...f, requiredRole: csv(detail.value) }))}
                placeholder="zenith, growth_platforms_technology"
              />
            </FormField>
            <Toggle checked={form.aiEnabled} onChange={({ detail }) => setForm((f) => ({ ...f, aiEnabled: detail.checked }))}>
              AI enabled
            </Toggle>
            <Toggle checked={form.emailEnabled} onChange={({ detail }) => setForm((f) => ({ ...f, emailEnabled: detail.checked }))}>
              Email enabled
            </Toggle>
            <FormField
              label="Email link domains"
              description="Hosts this app's emails may link to, comma-separated. Use *.example.deloitte.com to allow subdomains. Links to the App Store itself are always allowed."
              errorText={emailDomainsError()}
            >
              <Input
                value={form.emailLinkDomains}
                onChange={({ detail }) => setForm((f) => ({ ...f, emailLinkDomains: detail.value }))}
                placeholder="jupiter.deloitte.com"
              />
            </FormField>
            <FormField
              label="Email service role ARN"
              description="The IAM role this app's backend uses to send email without a signed-in user (infra/app-email-role.yaml). Leave blank if it only sends from the browser."
              errorText={emailRoleError()}
            >
              <Input value={form.emailServiceRoleArn} onChange={({ detail }) => setForm((f) => ({ ...f, emailServiceRoleArn: detail.value }))} />
            </FormField>
            <FormField label="Email daily cap" description="Recipients per day. Blank uses the platform default." errorText={emailCapError()}>
              <Input
                value={form.emailDailyCap}
                inputMode="numeric"
                onChange={({ detail }) => setForm((f) => ({ ...f, emailDailyCap: detail.value }))}
              />
            </FormField>
            <FormField
              label="Category"
              description="Where the app appears in the App Store filters. It also sets the tile color."
            >
              <Select
                selectedOption={CATEGORY_OPTIONS.find((o) => o.value === form.category) ?? CATEGORY_OPTIONS[0] ?? null}
                options={CATEGORY_OPTIONS}
                onChange={({ detail }) =>
                  setForm((f) => ({ ...f, category: (detail.selectedOption.value ?? '') as AppCategoryId | '' }))
                }
              />
            </FormField>
            <FormField
              label="Tagline"
              description="Shown on the App Store tile instead of the description."
              constraintText={`${form.tagline.trim().length}/${TAGLINE_MAX}`}
              errorText={taglineError()}
            >
              <Input value={form.tagline} onChange={({ detail }) => setForm((f) => ({ ...f, tagline: detail.value }))} />
            </FormField>
            <FormField label="Owner team" description="The team that owns the app, shown on the tile." errorText={ownerTeamError()}>
              <Input
                value={form.ownerTeam}
                onChange={({ detail }) => setForm((f) => ({ ...f, ownerTeam: detail.value }))}
                placeholder="e.g. GP Technology"
              />
            </FormField>
            <FormField label="Stage" description="How finished the app is, stated on its App Store tile.">
              <Select
                selectedOption={STAGE_OPTIONS.find((o) => o.value === form.lifecycle) ?? STAGE_OPTIONS[0] ?? null}
                options={STAGE_OPTIONS}
                onChange={({ detail }) =>
                  setForm((f) => ({ ...f, lifecycle: (detail.selectedOption.value ?? '') as AppLifecycleId | '' }))
                }
              />
            </FormField>
            <FormField label="Description">
              <Input value={form.description} onChange={({ detail }) => setForm((f) => ({ ...f, description: detail.value }))} />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>

      <Modal
        visible={deleteTarget !== null}
        onDismiss={() => setDeleteTarget(null)}
        header="Delete app?"
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button variant="primary" loading={deleting} onClick={confirmDelete}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {deleteTarget ? (
          <Box>
            This permanently removes <strong>{deleteTarget.displayName}</strong> ({deleteTarget.appId}) from the registry. This
            cannot be undone — the app will need to be re-registered to appear again.
          </Box>
        ) : null}
      </Modal>
    </PageLayout>
  );
}
